import { set_token, set_user_details, set_user_profile, fetch_and_store_user_profile } from '@/router';

function get_role_name(user) {
  return user.user_role?.role?.name || null;
}

function get_pending_user() {
  const pending_user_data_str = sessionStorage.getItem('pending_user_data');
  return pending_user_data_str ? JSON.parse(pending_user_data_str) : {};
}

function clear_pending_login() {
  sessionStorage.removeItem('pending_user_data');
  sessionStorage.removeItem('pending_username');
  sessionStorage.removeItem('pending_challenge_token');
  sessionStorage.removeItem('pending_login_password');
}

async function store_authenticated_user(data, fallback_user = {}) {
  if (!data.token) {
    throw new Error('No authentication token received.');
  }

  const user = data.user || fallback_user;

  set_token(data.token);
  set_user_details({
    role_type: get_role_name(user),
    user_id: user.id,
    first_name: user.first_name || '',
    last_name: user.last_name || ''
  });
  set_user_profile(user);
  await fetch_and_store_user_profile();
}

async function complete_token_login(context, data) {
  await store_authenticated_user(data);
  context.clear_pending_login();
  context.$router.push('/home');
}

function get_resend_request(context, client_app) {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({
      username: context.email,
      password: sessionStorage.getItem('pending_login_password'),
      client_app
    })
  };
}

function store_pending_challenge(context, data) {
  sessionStorage.setItem('pending_challenge_token', data.challenge_token);
  sessionStorage.setItem('pending_user_data', JSON.stringify(data.user || {}));
  context.otp = ['', '', '', '', '', ''];
  context.start_timer();
  context.set_success_message('A new verification code has been sent.');
}

async function resend_otp(context, client_app) {
  if (context.is_loading) {
    return;
  }

  if (!context.email || !sessionStorage.getItem('pending_login_password')) {
    context.set_error_message('Session expired. Please log in again.');
    context.clear_pending_login();
    return;
  }

  const response = await fetch('/aic_auth_api/system_management_api/login_api/', get_resend_request(context, client_app));
  const data = await response.json().catch(() => ({}));

  if (data.otp_required === false || data.token) {
    await context.complete_token_login(data);
    return;
  }

  if (!response.ok || data.status !== 'success' || !data.challenge_token) {
    throw new Error(data.error || data.message || 'Failed to resend OTP.');
  }

  store_pending_challenge(context, data);
}

function get_otp_session(context) {
  const otp_code = context.get_otp_code();

  if (otp_code.length !== 6) {
    context.set_error_message('Please enter the full 6-digit code.');
    return null;
  }

  const pending_username = sessionStorage.getItem('pending_username');
  const pending_challenge_token = sessionStorage.getItem('pending_challenge_token');

  if (!pending_username || !pending_challenge_token) {
    context.set_error_message('Session expired. Please log in again.');
    return null;
  }

  return ({ otp_code, pending_username, pending_challenge_token });
}

function get_verify_request(otp_session, client_app) {
  return ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      username: otp_session.pending_username,
      client_app,
      challenge_token: otp_session.pending_challenge_token,
      token: otp_session.otp_code
    })
  });
}

async function handle_verify_otp(context, client_app) {
  if (context.is_loading) {
    return;
  }

  context.clear_error_message();
  const otp_session = get_otp_session(context);
  if (!otp_session) {
    return;
  }

  try {
    context.is_loading = true;
    const response = await fetch('/aic_auth_api/system_management_api/otp/verify/', get_verify_request(otp_session, client_app));

    if (!response.ok) {
      throw new Error('Invalid or expired verification code.');
    }

    const data = await response.json().catch(() => ({}));
    await store_authenticated_user(data, get_pending_user());
    context.clear_pending_login();

    setTimeout(() => {
      context.$router.push('/home');
    }, 500);
  } catch (error) {
    context.set_error_message(error.message || 'Invalid OTP. Please try again.');
    context.otp = ['', '', '', '', '', ''];
  } finally {
    context.is_loading = false;
  }
}

export default function create_auth_methods(client_app) {
  return ({
    clear_pending_login() {
      clear_pending_login();
    },

    async complete_token_login(data) {
      await complete_token_login(this, data);
    },

    async resend_otp() {
      await resend_otp(this, client_app);
    },

    resendOTP() {
      return this.resend_otp();
    },

    async handle_verify_otp() {
      await handle_verify_otp(this, client_app);
    },

    submit_otp() {
      return this.handle_verify_otp();
    },

    cancel_verification() {
      this.clear_pending_login();
      this.$router.push('/login');
    }
  });
}
